import { useState, useEffect, useCallback } from "react";
import {
  Icon,
  apiFetch,
  apiPost,
  Loader,
  ini,
  avaClass,
  TECH_API,
} from "./apihandler";

function TechniciansPage({
  camp,
  onBack,
  onNext,
  onSelectTechnician,
}) {
  const [assignments, setAssignments] = useState([]);
  const [allTechs, setAllTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swapOpen, setSwapOpen] = useState({});
  const [swapTarget, setSwapTarget] = useState({});
  const [saving, setSaving] = useState({});
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setLoading(true);

    Promise.all([
      apiFetch(
        `${TECH_API}/camp/${camp.id}/assignments/`
      ),
      apiFetch(
        `${TECH_API}/technicians/`
      ),
    ])
      .then(([asgn, techs]) => {
        setAssignments(
          Array.isArray(asgn)
            ? asgn
            : asgn.results || []
        );

        setAllTechs(
          Array.isArray(techs)
            ? techs
            : techs.results || []
        );

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [camp.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSwap = (id) =>
    setSwapOpen((p) => ({
      ...p,
      [id]: !p[id],
    }));

  const confirmReassign = async (asgn) => {
    const newId = swapTarget[asgn.id];

    if (!newId) return;

    setSaving((p) => ({
      ...p,
      [asgn.id]: true,
    }));

    try {
      await apiPost(
        `${TECH_API}/reassign-technician/`,
        {
          assignment_id: asgn.id,
          new_technician_id: newId,
          camp_id: camp.id,
        }
      );

      setToast("Reassignment saved ✓");

      setTimeout(() => {
        setToast(null);
      }, 2500);

      load();
    } catch {
      setToast("Reassignment failed");

      setTimeout(() => {
        setToast(null);
      }, 2500);
    } finally {
      setSaving((p) => ({
        ...p,
        [asgn.id]: false,
      }));

      setSwapOpen((p) => ({
        ...p,
        [asgn.id]: false,
      }));
    }
  };

  const handlePricingClick = (asgn) => {
    const technicianId =
      asgn.technician ||
      asgn.technician_id;

    const serviceId =
      asgn.service ||
      asgn.service_id;

    console.log("Selected Assignment", {
      technicianId,
      campId: camp.id,
      serviceId,
    });

    onSelectTechnician({
      technicianId,
      campId: camp.id,
      serviceId,
    });
  };

  return (
    <div className="page">
      <div className="ph">
        <Icon n="users" size={18} />
        {camp.name ||
          `Camp #${camp.id}`}
        <span className="ph-sub">
          — technician assignments
        </span>
      </div>

      <div className="info-note">
        <Icon n="info" size={15} />
        <span>
          Assignments are pulled
          from{" "}
          <code
            style={{
              fontSize: 11,
              background: "#B5D4F4",
              padding: "1px 5px",
              borderRadius: 4,
            }}
          >
            TechnicianServiceAssignment
          </code>
          . You can reassign a
          service to a different
          technician if needed.
        </span>
      </div>

      {toast && (
        <div
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background:
              "var(--text)",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: 8,
            fontSize: 13,
            zIndex: 999,
            boxShadow:
              "0 4px 16px rgba(0,0,0,.2)",
          }}
        >
          {toast}
        </div>
      )}

      {loading && (
        <Loader text="Loading assignments…" />
      )}

      {!loading &&
        assignments.length === 0 && (
          <div className="empty">
            No assignments found for
            this camp.
          </div>
        )}

      {!loading &&
        assignments.map((asgn, i) => {
          const techName =
            asgn.technician_name ||
            asgn.technician?.name ||
            `Tech #${
              asgn.technician ||
              asgn.technician_id
            }`;

          const services =
            asgn.services ||
            asgn.service_names ||
            (asgn.service_name
              ? [asgn.service_name]
              : []);

          const empType =
            asgn.technician_type ||
            asgn.technician
              ?.employee_type ||
            "";

          const rate =
            asgn.rate_label ||
            asgn.pricing_label ||
            "";

          const isExternal =
            empType.toLowerCase() ===
            "external";

          return (
            <div
              className="tech-row"
              key={asgn.id || i}
            >
              <div
                className={`ava ${avaClass(
                  i
                )}`}
              >
                {ini(techName)}
              </div>

              <div className="tech-info">
                <div className="tech-meta">
                  <span className="tech-name">
                    {techName}
                  </span>

                  <span
                    className={`pill ${
                      isExternal
                        ? "pill-blue"
                        : "pill-gray"
                    }`}
                  >
                    {empType || "—"}
                  </span>

                  {rate && (
                    <span
                      style={{
                        fontSize: 11,
                        color:
                          "var(--text2)",
                      }}
                    >
                      {rate}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    marginTop: 6,
                  }}
                >
                  {services.map(
                    (s, j) => (
                      <span
                        className="svc-chip"
                        key={j}
                      >
                        <Icon
                          n="check"
                          size={11}
                        />
                        {s}
                      </span>
                    )
                  )}

                  {!services.length && (
                    <span
                      style={{
                        fontSize: 11,
                        color:
                          "var(--text3)",
                      }}
                    >
                      No services
                      listed
                    </span>
                  )}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() =>
                      toggleSwap(
                        asgn.id || i
                      )
                    }
                  >
                    <Icon
                      n="arrows"
                      size={13}
                    />
                    Reassign
                  </button>

                  <button
                    className="btn btn-sm btn-pri"
                    onClick={() =>
                      handlePricingClick(
                        asgn
                      )
                    }
                  >
                    <Icon
                      n="dollar"
                      size={13}
                    />
                    Configure Pricing
                  </button>
                </div>

                {swapOpen[
                  asgn.id || i
                ] && (
                  <div className="swap-area">
                    <label>
                      Swap technician
                      for these
                      services at
                      this camp:
                    </label>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap:
                          "wrap",
                        alignItems:
                          "center",
                      }}
                    >
                      <select
                        className="swap-select"
                        value={
                          swapTarget[
                            asgn.id
                          ] || ""
                        }
                        onChange={(
                          e
                        ) =>
                          setSwapTarget(
                            (
                              p
                            ) => ({
                              ...p,
                              [asgn.id]:
                                e
                                  .target
                                  .value,
                            })
                          )
                        }
                      >
                        <option value="">
                          — Select
                          technician —
                        </option>

                        {allTechs
                          .filter(
                            (
                              t
                            ) =>
                              t.id !==
                              (asgn.technician ||
                                asgn.technician_id)
                          )
                          .map(
                            (
                              t
                            ) => (
                              <option
                                key={
                                  t.id
                                }
                                value={
                                  t.id
                                }
                              >
                                {t.name ||
                                  t.full_name ||
                                  `Tech #${t.id}`}
                              </option>
                            )
                          )}
                      </select>

                      <button
                        className="btn btn-sm btn-pri"
                        disabled={
                          !swapTarget[
                            asgn.id
                          ] ||
                          saving[
                            asgn.id
                          ]
                        }
                        onClick={() =>
                          confirmReassign(
                            asgn
                          )
                        }
                      >
                        {saving[
                          asgn.id
                        ]
                          ? "Saving…"
                          : "Confirm Swap"}
                      </button>

                      <button
                        className="btn btn-sm"
                        onClick={() =>
                          toggleSwap(
                            asgn.id ||
                              i
                          )
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

      <div className="btn-between">
        <button
          className="btn"
          onClick={onBack}
        >
          <Icon
            n="arrowL"
            size={15}
          />
          Change Camp
        </button>

        <button
          className="btn btn-pri"
          onClick={onNext}
        >
          View Work Progress
          <Icon
            n="arrowR"
            size={15}
          />
        </button>
      </div>
    </div>
  );
}

export default TechniciansPage;